import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { unitById } from "@zend/content";
import { pick, useT } from "../i18n/index.js";
import { Button } from "../ui/Button.js";
import { Dialog } from "../ui/Dialog.js";
import { formatCompact } from "../ui/Counter.js";
import { errorText } from "../base/state.js";
import { requestScope, onRequestReadReset } from "../api/scope.js";
import { onWs } from "../api/ws.js";
import { worldApi } from "../world/api.js";
import { portraitUrl } from "../assets/manifest.js";
import { unitsApi } from "./api.js";
import { FormationSeat } from "./FormationSeat.js";
import { FormationPreview } from "./FormationPreview.js";
import { useUnits } from "./state.js";
import { Tabs, Stars, unitName } from "./shared.js";
import { SQUAD_SLOT_KEYS, type SquadSlotKey, type UnitView } from "./types.js";
import { SLOT_ROLES, assignSlot, slotCandidates, squadReadiness, formationEffects } from "./formationModel.js";
import { RoleIcon, StatIcon } from "../assets/gameIcons.js";
import { Icon } from "../ui/Icon.js";
import { navigate } from "../router.js";
import "./formation.css";
import { SquadTactics } from "./SquadTactics.js";

const LINK_COLOR: Record<string, string> = { strong: "#22c55e", commander: "#f5c542", rival: "#ef4444" };

/** Reads the existing reservation boundary, without inventing client-side combat rules. */
function useDeployedSquads() {
  const [deployed, setDeployed] = useState<Set<number> | null>(null);
  const [failed, setFailed] = useState(false);
  const [retry, setRetry] = useState(0);
  useEffect(() => {
    let alive = true, sequence = 0;
    const refresh = async () => {
      const current = ++sequence, generation = requestScope().generation;
      try {
        const marches = await worldApi.marches();
        if (alive && current === sequence && generation === requestScope().generation) {
          setDeployed(new Set(marches.filter(march => march.state !== "done").map(march => march.squadSlot))); setFailed(false);
        }
      } catch {
        if (alive && current === sequence && generation === requestScope().generation) { setDeployed(null); setFailed(true); }
      }
    };
    void refresh();
    const off = onWs("march.update", () => void refresh());
    const offReset = onRequestReadReset(() => { sequence++; setDeployed(null); void refresh(); });
    return () => { alive = false; sequence++; off(); offReset(); };
  }, [retry]);
  return { deployed, failed, retry: () => { setFailed(false); setRetry(value => value + 1); } };
}

/** Every slot is deliberately reviewed and saved; only server-confirmed composition is displayed. */
export function Formation() {
  const t = useT();
  const roster = useUnits(s => s.roster);
  const slot = useUnits(s => s.squadSlot);
  const setSlot = useUnits(s => s.setSquadSlot);
  const run = useUnits(s => s.run);
  const select = useUnits(s => s.select);
  const [picking, setPicking] = useState<SquadSlotKey | null>(null);
  const [candidate, setCandidate] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState<string | null>(null);
  const [explain, setExplain] = useState<string | null>(null);
  const [showLinks, setShowLinks] = useState(false);
  const operation = useRef(0), busy = useRef(false), alive = useRef(true);
  const container = useRef<HTMLDivElement | null>(null);
  const slotRefs = useRef(new Map<SquadSlotKey, HTMLDivElement>());
  const [lines, setLines] = useState<{ x1: number; y1: number; x2: number; y2: number; color: string }[]>([]);
  const squad = roster?.squads.find(s => s.slot === slot) ?? null;
  const { deployed, failed, retry } = useDeployedSquads();
  const reserved = !!squad?.siegeRunId;
  const away = deployed?.has(slot) ?? false;
  const locked = reserved || away || !deployed;
  const units = new Map((roster?.units ?? []).map(unit => [unit.id, unit]));
  useEffect(() => {
    alive.current = true;
    const reset = () => { operation.current++; busy.current = false; setPending(false); setPicking(null); setError(null); setSaved(null); };
    const off = onRequestReadReset(reset);
    return () => { alive.current = false; operation.current++; off(); };
  }, []);
  const mutate = async (op: () => Promise<unknown>, message: string, needsHome = true) => {
    if (busy.current || reserved || (needsHome && (away || !deployed))) return;
    busy.current = true; setPending(true); setError(null); setSaved(null);
    const token = ++operation.current, generation = requestScope().generation;
    const current = () => alive.current && operation.current === token && generation === requestScope().generation;
    try {
      const result = await run(async () => {
        try { return await op(); }
        catch (failure) { if (current()) setError(errorText(failure)); throw failure; }
      });
      if (current() && result !== null) { setPicking(null); setSaved(message); }
    } finally {
      if (current()) { busy.current = false; setPending(false); }
    }
  };
  const openPicker = (key: SquadSlotKey) => {
    if (busy.current || locked) return;
    setCandidate(squad?.members[key] ?? null); setPicking(key); setError(null); setSaved(null);
  };

  const measure = () => {
    if (!container.current || !squad) return setLines([]);
    const box = container.current.getBoundingClientRect();
    const centre = (key: SquadSlotKey) => {
      const el = slotRefs.current.get(key);
      if (!el) return null;
      const r = el.getBoundingClientRect();
      return { x: r.left - box.left + r.width / 2, y: r.top - box.top + r.height / 2 };
    };
    const keyOf = new Map<string, SquadSlotKey>();
    for (const key of SQUAD_SLOT_KEYS) if (squad.members[key]) keyOf.set(squad.members[key]!, key);
    const out: typeof lines = [];
    const seen = new Set<string>();
    for (const [unitId, chem] of Object.entries(squad.chemistry.perUnit)) {
      const from = keyOf.get(unitId);
      if (!from) continue;
      for (const link of chem.links) {
        const to = keyOf.get(link.to);
        if (!to) continue;
        const id = [unitId, link.to].sort().join("|") + link.kind;
        if (seen.has(id)) continue;
        seen.add(id);
        const a = centre(from);
        const b = centre(to);
        if (a && b) out.push({ x1: a.x, y1: a.y, x2: b.x, y2: b.y, color: LINK_COLOR[link.kind] ?? "#fff" });
      }
    }
    setLines(out);
  };
  useLayoutEffect(measure, [squad]);
  useEffect(() => {
    const ro = new ResizeObserver(measure);
    if (container.current) ro.observe(container.current);
    return () => ro.disconnect();
  }, [squad]);

  if (!roster) return null;
  const slotIds = Array.from({ length: roster.maxSquads }, (_, i) => i + 1);
  const readiness = squadReadiness(squad, roster.units, deployed);
  const filled = Object.values(squad?.members ?? {}).filter(Boolean).length;
  const unavailable = (unit: UnitView) => unit.squad !== null && (deployed?.has(unit.squad) || roster.squads.some(other => other.slot === unit.squad && other.siegeRunId));
  const chosen = candidate ? units.get(candidate) : undefined;
  const chosenUnavailable = chosen ? unavailable(chosen) : false;
  const transfer = chosen?.squad !== null && chosen?.squad !== undefined && chosen.squad !== slot;
  const currentSlot = chosen && squad ? SQUAD_SLOT_KEYS.find(key => key !== picking && squad.members[key] === chosen.id) : undefined;
  const changed = picking !== null && candidate !== (squad?.members[picking] ?? null);
  const effects = squad ? formationEffects(squad.members, units) : null;
  const renderSlot = (key: SquadSlotKey) => {
    const id = squad?.members[key] ?? null;
    const unit = id ? units.get(id) : undefined;
    const chem = unit ? squad?.chemistry.perUnit[unit.id] : undefined;
    return (
      <div key={key} ref={el => { if (el) slotRefs.current.set(key, el); else slotRefs.current.delete(key); }} className={`formation-slot ${key}`} data-testid={`slot-${key}`}>
        <div className="formation-slot-label"><RoleIcon role={SLOT_ROLES[key][0]!} size={19} /><span>{t(`slot.${key}`)}</span></div>
        {unit ? <FormationSeat unit={unit} slot={key} chemistry={chem?.pct ?? 0} onChoose={() => locked ? select(unit.id) : openPicker(key)} onInspect={() => select(unit.id)} /> : <button type="button" className="formation-empty" disabled={locked || pending} onClick={() => openPicker(key)} data-testid={`slot-empty-${key}`}>
          <Icon name="plus" /><strong>{t("squad.choose")}</strong>
        </button>}
      </div>
    );
  };

  return (
    <div className="formation-editor" data-testid="formation" aria-busy={pending}>
      <div className="formation-heading">
        <div><h2 data-testid="squad-heading">{t("units.squad.n", { n: slot })}{squad?.name ? ` · ${squad.name}` : ""}</h2><p className="formation-steps">{pick({de:"Platz wählen · Einheit prüfen · Speichern",en:"Choose a slot · Review a unit · Save"})}</p></div>
        <div className="formation-status"><span className={`badge ${readiness === "ready" ? "ok" : "warn"}`} data-testid="squad-readiness">{t(`squad.state.${readiness}`)}</span><span className="badge">{t("squad.filled", { n: filled })}</span></div>
      </div>
      <div className="formation-command-row">
      <div className="formation-squad-tabs"><Tabs tabs={slotIds} active={slot} onChange={next => { if (!busy.current) { setSlot(next); setPicking(null); setError(null); setSaved(null); } }} label={number => t("units.squad.n", { n: number })} testPrefix="squad" /></div>
      <div className="formation-toolbar">
        <Button aria-label={t("squad.auto")} disabled={locked || pending} loading={pending && !picking} onClick={() => void mutate(() => unitsApi.autofill(slot), t("units.toast.autofill"))} data-testid="squad-autofill"><Icon name="people" /><span className="formation-auto-full">{t("squad.auto")}</span><span className="formation-auto-short" aria-hidden>Auto</span></Button>
      </div>
      </div>
      <div className="formation-deploy">
        <Button variant="primary" onClick={() => navigate("/app/expeditions")} data-testid="squad-to-expeditions">{t("squad.toExpeditions")}</Button>
        <Button onClick={() => navigate("/app/arena")} data-testid="squad-to-arena">{t("squad.toArena")}</Button>
      </div>
      {failed ? <div className="formation-notice" role="alert"><p>{t("squad.availabilityFailed")}</p><Button onClick={retry}>{t("squad.retry")}</Button></div> : null}
      {reserved ? <div className="formation-notice" data-testid="squad-siege-reserved"><p>{t("siege.formationReserved")}</p><Button onClick={() => navigate("/app/events")}>{t("siege.return")}</Button></div> : away ? <div className="formation-notice"><p>{t("squad.deployedHint")}</p><Button onClick={() => navigate("/app/map")}>{t("squad.toWorld")}</Button></div> : null}
      {readiness === "repairing" ? <p className="hint">{t("squad.repairHint")}</p> : null}
      {saved ? <p className="badge ok" role="status" data-testid="squad-saved"><Icon name="check" />{saved}</p> : null}
      {error && !picking ? <p className="formation-notice" role="alert">{error}</p> : null}
      {squad ? <div className="formation-summary">
        <span className="badge gold" data-testid="squad-power"><StatIcon stat="power" />{formatCompact(squad.power)}</span>
        {effects?.morale ? <span className="formation-morale">{pick({de:"Moral",en:"Morale"})} <b>{effects.morale.score}</b> · +{effects.morale.atkPct}% ATK</span> : null}
        <span className="hint">{t("units.chemistry")}</span><span className="progress" style={{ width: 120 }}><span className="progress-fill" style={{ display: "block", width: `${squad.chemistry.percent}%` }} /></span><strong className="mono" data-testid="squad-chemistry">{squad.chemistry.percent}%</strong>
        {squad.chemistry.doctrines.map(doctrine => <button key={doctrine} type="button" className="chip ok" onClick={() => setExplain(explain === doctrine ? null : doctrine)} data-testid={`doctrine-${doctrine}`}>{t(`doctrine.${doctrine}`)}</button>)}
        {squad.wrecked > 0 ? <span className="badge warn"><Icon name="warning" label={t("units.wrecked")} />{squad.wrecked}</span> : null}
      </div> : null}
      {explain ? <p className="hint formation-notice">{t(`doctrine.${explain}.hint`)} · {t("units.linkLegend")}</p> : null}
      <div className="formation" ref={container}>
        {showLinks ? <svg className="chem-lines" data-visualization="formation-chemistry" aria-hidden>{lines.map((line, index) => <line key={index} x1={line.x1} y1={line.y1} x2={line.x2} y2={line.y2} stroke={line.color} strokeWidth={3} strokeOpacity={0.8} strokeLinecap="round" />)}</svg> : null}
        <section className="formation-zone front" aria-label={t("squad.front")}><h3>{t("squad.front")}</h3><p className="hint">{pick({de:"F1 fängt normale Frontangriffe ab. Hier kämpfen Verteidiger, Angreifer und Saboteure.",en:"F1 takes normal front attacks. Defenders, attackers and saboteurs fight here."})}</p><div className="formation-zone-grid">{(["f1", "f2", "f3", "f4"] as SquadSlotKey[]).map(renderSlot)}</div></section>
        <section className="formation-zone rear" aria-label={t("squad.rear")}><h3>{t("squad.rear")}</h3><p className="hint">{pick({de:"Kommandant und Support stärken die Front.",en:"Commander and support strengthen the front."})}</p><div className="formation-zone-grid">{(["s1", "commander", "s2"] as SquadSlotKey[]).map(renderSlot)}</div></section>
      </div>
      {!squad?.members.commander ? <p className="hint" data-testid="squad-missing-commander"><Icon name="info" /> {t("squad.missingCommander")}</p> : null}
      <button type="button" className="formation-links-toggle" aria-pressed={showLinks} onClick={() => setShowLinks(v => !v)}>{pick({de:"Hersteller-Verbindungen",en:"Manufacturer links"})} {showLinks ? "−" : "+"}</button>
      {showLinks ? <p className="hint">{t("units.linkLegend")}</p> : null}
      <div className="formation-administration">{squad && !squad.garrison ? <Button disabled={reserved || pending || roster.squads.some(other => other.garrison && other.siegeRunId)} onClick={() => void mutate(() => unitsApi.garrison(slot), t("units.toast.garrison"), false)} data-testid="squad-garrison"><Icon name="shield" />{t("units.setGarrison")}</Button> : squad ? <span className="badge ok"><Icon name="shield" />{t("units.garrison")}</span> : null}</div>
      {squad ? <SquadTactics squad={squad} units={units} /> : null}
      <details className="field-details"><summary>{pick({de:"Weitere Truppplätze",en:"More squad slots"})}</summary><p className="hint">{pick({de:"Zwei unabhängige Trupps ab Kommandozentrale Stufe 1 (HQ 3). Trupp 3 / 4 ab Stufe 8 / 16. Jede Einheit gehört genau einem Team. Ein Ersatzteam braucht eigene Einheiten.",en:"Two independent squads at Command Center level 1 (HQ 3). Squads 3 / 4 at levels 8 / 16. Each unit belongs to one team. A reserve team needs its own units."})}</p></details>
      {picking ? <Dialog title={t("squad.pickerTitle", { slot: t(`slot.${picking}`) })} onClose={() => setPicking(null)} testId="slot-picker">
        <div className="formation-picker-layout">
          <div className="formation-picker-intro"><p>{pick({de:"Erst Übernehmen speichert deine Auswahl.",en:"Apply saves your selection."})}</p><small>{t("squad.allowed", { roles: SLOT_ROLES[picking].map(role => t(`role.${role}`)).join(" · ") })}</small></div>
          <div className="formation-candidates" aria-label={t("squad.choose")}>
            {slotCandidates(roster.units, picking).map(unit => {
              const role = unitById(unit.unitId).role;
              const blocked = unavailable(unit);
              return <button key={unit.id} type="button" className="formation-candidate" aria-pressed={candidate === unit.id} disabled={pending || !!blocked || locked} onClick={() => setCandidate(unit.id)} data-testid={`pick-${unit.unitId}`} data-row-id={unit.id}>
                <img src={portraitUrl(unit.unitId)} alt="" loading="lazy" decoding="async" />
                <span className="formation-candidate-copy"><strong>{unitName(unit.unitId)}</strong><span className="formation-candidate-meta"><RoleIcon role={role} size={17} />{t(`role.${role}`)}<StatIcon stat="power" size={15} />{formatCompact(unit.power)}<Stars stars={unit.stars} size={10} /></span><small>{blocked ? t("squad.unitReserved", { n: unit.squad! }) : unit.squad !== null ? t("units.inSquad", { n: unit.squad }) : t("squad.unassigned")}{unit.repair ? ` · ${t("units.repairing")}` : unit.wrecked ? ` · ${t("units.wrecked")}` : ""}</small></span>
                {candidate === unit.id ? <Icon name="check" label={t("squad.selected")} /> : <Icon name="plus" />}
              </button>;
            })}
            {slotCandidates(roster.units, picking).length === 0 ? <p className="hint">{t("units.noneEligible")}</p> : null}
          </div>
          <div className="formation-picker-footer">
            <p data-testid="slot-selection">{chosen ? t("squad.selection", { name: unitName(chosen.unitId) }) : t("squad.selectionEmpty")}</p>
            {changed && picking ? <FormationPreview before={assignSlot(squad, picking, squad?.members[picking] ?? null)} after={assignSlot(squad, picking, candidate)} units={units} /> : null}
            {transfer ? <p className="formation-picker-transfer" data-testid="slot-transfer">{t("squad.transfer", { name: unitName(chosen!.unitId), from: chosen!.squad!, to: slot })}</p> : currentSlot ? <p className="formation-picker-transfer">{t("squad.moveSlot", { from: t(`slot.${currentSlot}`) })}</p> : null}
            {chosen?.repair || chosen?.wrecked ? <p className="formation-picker-transfer">{t("squad.damagedHint")}</p> : null}
            {error ? <p role="alert" data-testid="slot-error">{error}</p> : null}
            {locked || chosenUnavailable ? <p role="status">{t("squad.changedReservation")}</p> : null}
            <div className="formation-picker-actions">
              {squad?.members[picking] ? <Button variant="ghost" disabled={pending} onClick={() => setCandidate(null)} data-testid="slot-clear">{t("units.slotClear")}</Button> : null}
              <Button variant="ghost" onClick={() => setPicking(null)} data-testid="slot-cancel">{t("common.cancel")}</Button>
              <Button variant="gold" loading={pending} disabled={!changed || locked || !!chosenUnavailable} onClick={() => void mutate(() => unitsApi.setSquad(slot, assignSlot(squad, picking, candidate)), t("units.toast.squad"))} data-testid="slot-apply">{t("squad.apply")}</Button>
            </div>
          </div>
        </div>
      </Dialog> : null}
    </div>
  );
}
